import { router, resolveRouterPath } from '../../router';
import { viewerCanOpen } from '../../utility/viewer-route-access';
import { terms } from '../../language/terms';

/**
 * Product Registry's routes, built in one place for every host of the list components (the
 * module's own list pages and the company workspace tabs), so a product opens at the
 * same address wherever it was clicked (D-102).
 *
 * The components emit navigation intent and never route; these are what the hosts call. The
 * paths are the ones registered in router.ts and are not invented here.
 */

/** The report types the list can start creating, spelled as the routes spell them. */
export type ProductReportCreateType = 'as2047' | 'as5218' | 'bushfire';

/** A product's detail page. */
export function productHref(productId: string): string {
  return resolveRouterPath(`module/product-registry/products/${productId}`);
}

/**
 * The create form for a product, carrying the register the list was scoped or narrowed to.
 *
 * Carried in the address rather than as state because routing tears the list down - nothing
 * but the address survives the crossing - and because it leaves the pre-filled form linkable
 * and refresh-proof. `router.ts` reads `?owner=` back off the query and passes it down.
 */
export function productCreateHref(ownerId?: string): string {
  const base = resolveRouterPath('module/product-registry/products/new');
  return ownerId ? `${base}?owner=${encodeURIComponent(ownerId)}` : base;
}

/**
 * A report's detail page. The route segment is the report type's own path piece, which the
 * list row carries from the server rather than the UI deriving it from the type.
 */
export function reportHref(routeSegment: string, reportId: string): string {
  return resolveRouterPath(
    `module/product-registry/reports/${routeSegment}/${reportId}`
  );
}

/**
 * The create form for a report.
 *
 * AS 2047 and AS 5218 have a module-level create page that picks its own product; a bushfire
 * report is product-scoped and has no `/new` of its own, so it is only reachable once a
 * product has been chosen - which is why the reports component owns that picker. The register
 * is NOT carried: every one of these forms takes its register from the product it is written
 * against, so an owner in the address would be a value nothing reads.
 */
export function reportCreateHref(detail: {
  reportType: ProductReportCreateType;
  productId?: string;
}): string {
  if (detail.reportType === 'bushfire') {
    if (!detail.productId) {
      throw new Error(
        `A bushfire report can only be created against a ${terms.product.lower}`
      );
    }
    return resolveRouterPath(
      `module/product-registry/products/${detail.productId}/reports/bushfire/new`
    );
  }
  return resolveRouterPath(
    `module/product-registry/reports/${detail.reportType}/new`
  );
}

/** The module's own products list. */
export function productsListHref(): string {
  return resolveRouterPath('module/product-registry/products');
}

/** The module's own test reports list. */
export function reportsListHref(): string {
  return resolveRouterPath('module/product-registry/reports');
}

/**
 * Whether the signed-in person may open the module's products list: what a workspace tab
 * asks before offering the way back to every register's products (architect, 2026-09-07,
 * extending the glass tabs' link: "we should do that on the product and test reports pages
 * also"). The tabs are staff routes; the list needs a supplier or data-entry domain and the
 * creator role, which not every staff member holds.
 */
export function canOpenProductsList(): boolean {
  return viewerCanOpen(productsListHref());
}

/**
 * The same question for the module's test reports list, which admits auditors as well, so
 * the two answers can differ for one person and are asked separately.
 */
export function canOpenReportsList(): boolean {
  return viewerCanOpen(reportsListHref());
}

/** The module's support-data settings. */
export function settingsHref(): string {
  return resolveRouterPath('module/product-registry/settings');
}

/** Route in-SPA, falling back to a history push when no router is mounted (tests). */
export function navigateTo(href: string): void {
  if (router) void router.navigate(href);
  else window.history.pushState({}, '', href);
}
